import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StudioXCredit } from '../StudioXCredit';
import { supabase } from '../../lib/supabase';
import {
  Users,
  LayoutGrid,
  LogOut,
  X,
  ChevronLeft,
  ChevronUp,
  User,
  UserCog,
  Mail,
  Phone,
  Building,
} from 'lucide-react';
import { ThemeToggle } from '../ThemeToggle';

// Context for child components to set header content
interface DeptAdminLayoutContextType {
  setHeaderAction: (action: React.ReactNode | null) => void;
  setPageTitle: (title: string | null) => void;
}

const DeptAdminLayoutContext = createContext<DeptAdminLayoutContextType | null>(null);

export function useDeptAdminLayoutContext() {
  const context = useContext(DeptAdminLayoutContext);
  if (!context) {
    throw new Error('useDeptAdminLayoutContext must be used within DeptAdminLayout');
  }
  return context;
}

export function DeptAdminLayout() {
  const { user, profile, signOut } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [headerAction, setHeaderAction] = useState<React.ReactNode | null>(null);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Get department info
  const [department, setDepartment] = useState<any | null>(null);
  useEffect(() => {
    if (!profile?.departmentId) return;
    supabase.from('departments').select('*').eq('id', profile.departmentId).single()
      .then(({ data }) => setDepartment(data));
  }, [profile?.departmentId]);

  // Get company info
  const [company, setCompany] = useState<any | null>(null);
  useEffect(() => {
    if (!department?.company_id) return;
    supabase.from('companies').select('*').eq('id', department.company_id).single()
      .then(({ data }) => setCompany(data));
  }, [department?.company_id]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleSignOut = async () => {
    localStorage.clear();
    sessionStorage.clear();
    await signOut();
    window.location.href = '/login';
  };

  const navigation = [
    { name: 'Oversikt', href: '/dept-admin', icon: LayoutGrid },
    { name: 'Brukere', href: '/dept-admin/users', icon: Users },
  ];

  const isActive = (href: string) => {
    if (href === '/dept-admin') {
      return location.pathname === '/dept-admin';
    }
    return location.pathname.startsWith(href);
  };

  const getRoleName = (role?: string) => {
    const roles: Record<string, string> = {
      superadmin: 'Systemadmin',
      editor: 'Redaktør',
      org_admin: 'Bedriftsadmin',
      dept_admin: 'Avdelingsadmin',
      counselor: 'Bruker',
    };
    return roles[role || ''] || role || 'Bruker';
  };

  return (
    <DeptAdminLayoutContext.Provider value={{ setHeaderAction, setPageTitle }}>
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'w-64' : 'w-0'
          } bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 overflow-hidden flex flex-col fixed h-full z-30`}
        >
          {/* Logo & Close */}
          <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-gray-900 dark:text-white">Avdeling</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Department Info */}
          {department && (
            <div className="px-4 py-3 border-b dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">Avdeling</p>
              <p className="font-semibold text-gray-900 dark:text-white truncate">{department.name}</p>
              {company && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">{company.name}</p>
              )}
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            <Link
              to="/assessment/start"
              className="flex items-center gap-3 px-3 py-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">Tilbake til vurdering</span>
            </Link>

            <div className="pt-4 pb-2">
              <p className="px-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                Min avdeling
              </p>
            </div>

            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    active
                      ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User info at bottom with menu */}
          <div ref={userMenuRef} className="p-4 border-t dark:border-gray-700 relative">
            {/* User Menu Dropdown */}
            {userMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 mx-4 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 overflow-hidden z-50">
                {/* Profile information */}
                <div className="px-4 py-3 space-y-2">
                  <div className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
                    <User className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <span className="text-sm font-medium truncate">{profile?.fullName || 'Ikke angitt'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                    <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <span className="text-sm truncate">{user?.email || 'Ikke angitt'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                    <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <span className="text-sm truncate">{profile?.phone || 'Ikke angitt'}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
                    <UserCog className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <span className="text-sm font-medium">{getRoleName(profile?.role)}</span>
                  </div>
                </div>
                
                {/* Profile link */}
                <Link
                  to="/profile"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors border-t border-gray-100 dark:border-gray-600"
                >
                  <User className="h-4 w-4" />
                  <span className="text-sm font-medium">Rediger profil</span>
                </Link>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-3 flex-1 min-w-0 p-2 -m-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              >
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {profile?.fullName || user?.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{getRoleName(profile?.role)}</p>
                </div>
                <ChevronUp className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              <button
                onClick={handleSignOut}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Logg ut"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-0'} transition-all duration-300`}>
          {/* Top header */}
          <header className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700 sticky top-0 z-20">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                {!sidebarOpen && (
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <LayoutGrid className="w-5 h-5" />
                  </button>
                )}
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {pageTitle || department?.name || 'Avdelingsadministrasjon'}
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <ThemeToggle />
                {headerAction}
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="p-6">
            <Outlet />
            <StudioXCredit />
          </main>
        </div>
      </div>
    </DeptAdminLayoutContext.Provider>
  );
}
